import axios from "axios";
import { emitAccessDenied } from "../utils/accessDenied";
import { getDeviceId } from "../utils/device";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api",
  withCredentials: true,
  timeout: Number(process.env.REACT_APP_API_TIMEOUT) || 15000,
});

// Session codes returned by the gateway when a session is missing or ended.
const SESSION_END_CODES = new Set([
  "NOT_AUTHENTICATED",
  "DEVICE_REQUIRED",
  "SESSION_INVALID",
  "SESSION_REVOKED",
  "SESSION_EXPIRED",
  "SESSION_DEVICE_MISMATCH",
  "ACCOUNT_INACTIVE",
]);

api.interceptors.request.use((config) => {
  config.headers["X-Client-App"] = "admin";
  config.headers["X-Device-Id"] = getDeviceId();
  return config;
});

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const responseData = error.response?.data || {};

    /*
     * Session ended (signed out elsewhere, expired, or revoked by an admin):
     * back to the login screen.
     */
    if (SESSION_END_CODES.has(responseData.code)) {
      window.dispatchEvent(
        new CustomEvent("auth-session-ended", {
          detail: { code: responseData.code },
        })
      );

      if (!originalRequest?.skipAuthRedirect && window.location.pathname !== "/login") {
        window.location.replace("/login");
      }

      return Promise.reject(error);
    }

    /*
     * Permission denied
     */
    const isAuthRoute = String(originalRequest?.url || "").startsWith("/auth/");

    if (status === 403 && !isAuthRoute) {
      const headers = originalRequest?.headers || {};

      emitAccessDenied({
        message:
          responseData.message ||
          "You do not have permission to access this page or perform this action.",

        pageName:
          headers["x-page-name"] ||
          headers["X-Page-Name"] ||
          null,

        action:
          headers["x-action"] ||
          headers["X-Action"] ||
          null,
      });
    }

    return Promise.reject(error);
  }
);

export default api;
